"use client";

import { useEffect } from "react";

import {
  DEFAULT_CAPTCHA_LOG_PATH,
  reportCaptchaEvent,
  type CaptchaEventPayload,
} from "./log";

// AWS WAF CAPTCHA JS API (AwsWafCaptcha.renderCaptcha). Served from the
// CloudFront (edge) captcha-sdk host — a different host+path from the
// challenge.js token SDK in sdk.tsx. marketing_site_ddos_protection is
// CLOUDFRONT-scoped, so use the edge URL, not the us-east-1 regional one.
const CAPTCHA_JS_URL =
  "https://380ce6d4f3e0.edge.captcha-sdk.awswaf.com/380ce6d4f3e0/jsapi.js";

// Public, domain-scoped WAF application-integration key (not a secret; ships in
// browser JS) — same category as the challenge.js URL above. Minted by
// aws_wafv2_api_key.marketing_site_captcha (infra: services/intercom/waf).
const API_KEY =
  "J6R1lpDMM7bdmzTIxyl7rJFGRfKYptvWsVJYbZRMuO4CQch2WRdlg5BtBx7gC/yZb1EtoIdpKPz3kApZZ9i3AASR6VmKg6eJz3gSV+vzyVxu+zDNvh73rPDfrZn1Xb/Hn5LLRryM9zOrOXGvojcPreSsjsZ0kFrF4IcsZq37dx57zYzcnbd+9Pe7GT42DtgpvOmSrteNwvR3HBZ8KCrLzjWcORQu9Y5YXBna0p+gr38A08VIfPH89eWYt5dyWKNXMomeWjP3ioTjP0R5oaDIVacAKWvu+Zf2SJcaNxknauF+6Jr65+yeii4kNSv0rG1GSMj7kqZGWM+N4zq091sXcYkWCtuN03/o5WU6bpjJtd0fMSlB4g34JSWNsXgFzD/PxFljvrvK7ijkizEyz1vLm/JKdWHycogRIjl8+RozVr/YDzp9TxlNA8FXhX90GXixZMs2YoZrrqfFe3rAfjIJKg/nGHR+bNdBcWb87YyZLme17eo5ym/ZPlma88esPyi70dEquD9CizCjCt2CcfIDTOEWiAK1Im7A3ah3YEvrG9pehHzdmI8QOr706e99JFG+Ed+286xyeF+nMGw4vkuMDVVoM5VPKG69tOlqiFSF4tyIt7Tcn2H8Hupyc0HwrYYhlZ5nr2dsxC6ljNggKVqsz15Jl+fngSkGo5Le8ohCpcM=_1_1";

type RenderCaptcha = (
  container: HTMLElement,
  options: {
    apiKey: string;
    onSuccess: (token: string) => void;
    onError?: (error: unknown) => void;
    dynamicWidth?: boolean;
  },
) => void;

declare global {
  interface Window {
    AwsWafCaptcha?: { renderCaptcha: RenderCaptcha };
    __awsWafCaptchaGateInstalled?: boolean;
  }
}

let captchaJsPromise: Promise<void> | null = null;

// challenge.js loads eagerly (AwsWafSdk, every page); captcha.js is the rare
// interactive-puzzle path, so inject it only on the first challenge and memoise.
function ensureCaptchaJs(): Promise<void> {
  if (window.AwsWafCaptcha) return Promise.resolve();
  captchaJsPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = CAPTCHA_JS_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      captchaJsPromise = null;
      reject(new Error("Failed to load AWS WAF captcha.js"));
    };
    document.head.appendChild(script);
  });
  return captchaJsPromise;
}

function isCaptchaResponse(response: Response): boolean {
  return (
    response.status === 405 &&
    response.headers.get("x-amzn-waf-action") === "captcha"
  );
}

// Browser telemetry POSTs across the four apps — mirrors the marketing-sites WAF
// carve-outs (marketing_site_telemetry_paths / _s2s_paths). Never /api wholesale.
const TELEMETRY_PATH_PREFIXES = [
  "/visitor_analytics", // monolith visitor analytics: pageviews, leads, experiment assignments
  "/api/visitor_analytics", // monolith visitor analytics API
  "/api/eppo/log-assignment", // Eppo assignment logger beacon
  "/_vercel/", // Vercel Speed Insights (web vitals) + Web Analytics
];

const TELEMETRY_PATHS_EXACT = new Set([
  "/monitoring", // Sentry browser SDK tunnel (fin-ai, intercom-com, pioneer)
  "/signup/monitoring", // Sentry tunnel on signup (tunnelRoute override)
  "/signup/client-logs", // signup client error/telemetry sink
  "/api/captcha-log", // this gate's own event beacon (fin-ai, intercom-com)
  "/signup/api/captcha-log", // ...and its signup-prefixed equivalent
]);

function requestPathname(input: RequestInfo | URL): string | null {
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input instanceof Request
          ? input.url
          : "";
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return null;
  }
}

function isTelemetryPath(input: RequestInfo | URL): boolean {
  const pathname = requestPathname(input);
  if (!pathname) return false;
  return (
    TELEMETRY_PATHS_EXACT.has(pathname) ||
    TELEMETRY_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

// A submit worth interrupting with a puzzle: a POST that isn't a background
// beacon. POST-only because a challenged form submit is the case React can't
// recover on its own (the server-action runtime discards the 405 CAPTCHA body
// and throws). !keepalive drops analytics/telemetry beacons (e.g. gtm-js ->
// /visitor_analytics, signup metrics/logs) — a beacon has no one waiting on it,
// so popping a puzzle for one is nonsense; keepalive is their common signal.
//
// This leans on a WAF-side contract: CAPTCHA rules must not challenge beacon /
// analytics endpoints. A rule that is NOT method-specific is fine — the user's
// GET page load trips it first and WAF's own full-page challenge issues the
// token (24h immunity), so the later POST already carries it and never reaches
// this gate. The gate only earns its keep for the residual POST-first / expired-
// token case.
function isInterruptibleSubmit(
  input: RequestInfo | URL,
  init?: RequestInit,
): boolean {
  const fromRequest = input instanceof Request ? input : undefined;
  const method = (init?.method ?? fromRequest?.method ?? "GET").toUpperCase();
  const keepalive = init?.keepalive ?? fromRequest?.keepalive ?? false;
  if (method !== "POST" || keepalive) return false;
  return !isTelemetryPath(input);
}

type Report = (payload: CaptchaEventPayload) => void;

function describeError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.slice(0, 200);
}

function renderCaptchaOverlay(report: Report): Promise<void> {
  return ensureCaptchaJs().then(
    () =>
      new Promise<void>((resolve, reject) => {
        const captcha = window.AwsWafCaptcha;
        // Checked before the overlay goes up: a script that loads without
        // defining the global would otherwise leave the page dimmed and blocked.
        if (!captcha) {
          reject(new Error("AWS WAF captcha.js loaded without AwsWafCaptcha"));
          return;
        }
        const overlay = document.createElement("div");
        overlay.style.cssText =
          "position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.5)";
        const container = document.createElement("div");
        container.style.cssText =
          "background:#fff;padding:20px;border-radius:12px;width:min(92vw, 460px)";
        overlay.appendChild(container);
        document.body.appendChild(overlay);

        const shownAt = Date.now();
        report({ event: "captcha_shown" });

        captcha.renderCaptcha(container, {
          apiKey: API_KEY,
          dynamicWidth: true,
          onSuccess: () => {
            overlay.remove();
            report({
              event: "captcha_solved",
              durationMs: Date.now() - shownAt,
            });
            resolve();
          },
          onError: (error) => {
            overlay.remove();
            report({
              event: "captcha_failed",
              durationMs: Date.now() - shownAt,
              reason: describeError(error),
            });
            reject(error);
          },
        });
      }),
    (error) => {
      report({
        event: "captcha_sdk_load_failed",
        reason: describeError(error),
      });
      throw error;
    },
  );
}

// Single-flight: concurrent challenged submits (double-click, two forms) share
// one overlay. Once solved, every waiter's retry fires carrying the same token.
// The overlay's own events are attributed to whichever submit opened it — one
// puzzle is one captcha_shown — while each waiter still reports its own retry.
let pendingSolve: Promise<void> | null = null;

function solveCaptcha(report: Report): Promise<void> {
  pendingSolve ??= renderCaptchaOverlay(report).finally(() => {
    pendingSolve = null;
  });
  return pendingSolve;
}

export function installCaptchaFetchGate(
  logPath: string = DEFAULT_CAPTCHA_LOG_PATH,
): void {
  if (window.__awsWafCaptchaGateInstalled || !API_KEY) return;
  window.__awsWafCaptchaGateInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const gated = isInterruptibleSubmit(input, init);
    // Clone before the first send: a streamed request body can only be read
    // once, so the retry needs its own copy.
    const retryRequest =
      gated && input instanceof Request ? input.clone() : null;

    const response = await originalFetch(input, init);
    if (!gated || !isCaptchaResponse(response)) return response;

    const path = requestPathname(input) ?? undefined;
    const report: Report = (payload) =>
      reportCaptchaEvent(logPath, { path, ...payload });

    await solveCaptcha(report);
    const retry = await originalFetch(retryRequest ?? input, init);
    report({ event: "captcha_retry_result", status: retry.status });
    return retry;
  };
}

// Patches window.fetch so a POST that trips a WAF CAPTCHA rule (405 +
// x-amzn-waf-action: captcha) renders the puzzle inline and retries once
// solved, instead of the browser discarding the 405. No-op without an API key.
//
// Each step of that flow beacons to `logPath` so the puzzle is observable in
// Datadog. Pass logPath on an app whose routes are prefixed (signup) — fetch is
// not basePath-aware.
export function AwsWafCaptchaGate({
  logPath = DEFAULT_CAPTCHA_LOG_PATH,
}: {
  logPath?: string;
} = {}) {
  useEffect(() => {
    installCaptchaFetchGate(logPath);
  }, [logPath]);
  return null;
}
